/**
 * @file    js/slideshow.js
 *
 * adds a custom slideshow
 *
 * refactored version of depage-jquery-plugins/depage-slideshow.js
 * without jquery
 *
 * copyright (c) 2006-2026 Frank Hellenkamp [jonas@depage.net] *
 * @author    Frank Hellenkamp [jonas@depage.net]
 */

// {{{ defaultOptions
export const defaultOptions = {
    elements: "div, span, img",
    speed: 3000,
    pause: 3000,
    startPos: 0,
    waitForImagesToLoad: true,
};
// }}}

var canTransition = "transition" in document.documentElement.style;

// stores the still pending transitionend listener of an element,
// so it can be replaced when a new transition is started (like $.off().one())
var transitionListeners = new WeakMap();

// {{{ oneTransition()
var oneTransition = function(el, callback) {
    var old = transitionListeners.get(el);
    if (old) {
        el.removeEventListener("transitionend", old);
    }

    var listener = function(e) {
        // only react to the opacity transition of the slide itself,
        // not to transitions of child elements bubbling up
        if (e.target !== el || e.propertyName !== "opacity") return;

        el.removeEventListener("transitionend", listener);
        transitionListeners.delete(el);
        callback();
    };

    transitionListeners.set(el, listener);
    el.addEventListener("transitionend", listener);
};
// }}}

// reverse references to the slideshow instances (replaces $el.data("depage.slideshow"))
var instances = new WeakMap();

export class Slideshow {
    /* {{{ constructor */
    constructor(el, options) {
        if (instances.has(el)) {
            // this element is already a slideshow
            // @todo remove and re-add slideshow when called with different options
            return instances.get(el);
        }

        this.el = el;
        instances.set(el, this);

        this.options = { ...defaultOptions, ...options };
        this.options.speed = Number(el.dataset.slideshowSpeed) || this.options.speed;
        this.options.pause = Number(el.dataset.slideshowPause) || this.options.pause;
        this.options.startPos = Number(this.options.startPos) || 0;

        this.activeSlide = 0;
        this.playing = false;
        this.num = 0;
        this.timer = null;

        this.divs = Array.from(el.children).filter(function(child) {
            return child.matches(this.options.elements);
        }, this);
        this.num = this.divs.length;

        // make parent "not static"
        if (getComputedStyle(el).position == "static") {
            el.style.position = "relative";
        }

        var wasAbsolute = this.divs.length > 0 && getComputedStyle(this.divs[0]).position == "absolute";

        // make children "absolute"
        this.divs.forEach(function(div) {
            Object.assign(div.style, {
                position: "absolute",
                left: "0",
                top: "0"
            });
        });
        // make first child "static" to have an automatic height
        if (!wasAbsolute && this.divs.length > 0) {
            this.divs[0].style.position = "static";
        }
        this.divs.forEach((div, i) => {
            if (i != this.options.startPos) {
                Object.assign(div.style, {
                    visibility: "hidden",
                    opacity: 0
                });
            }
        });

        if (this.divs.length > 1) {
            this.activeSlide = this.options.startPos;
            this.playing = true;
            this.waitForNext();

            setTimeout(() => {
                this.dispatch("show", { num: this.options.startPos, total: this.divs.length });
            }, 10);
        }
    }
    /* }}} */

    /* {{{ dispatch() */
    dispatch(name, detail) {
        this.el.dispatchEvent(new CustomEvent("depage-slideshow:" + name, { detail: detail || {} }));
    }
    /* }}} */

    /* {{{ clearQueue() */
    clearQueue() {
        clearTimeout(this.timer);
        this.timer = null;
    }
    /* }}} */
    /* {{{ waitForNext() */
    waitForNext() {
        this.clearQueue();

        if (this.options.pause != -1) {
            this.timer = setTimeout(() => {
                if (this.playing) {
                    this.next();
                }
            }, this.options.pause);
        }
    }
    /* }}} */
    /* {{{ play() */
    play() {
        this.dispatch("play");

        this.playing = true;
        this.next();
    }
    /* }}} */
    /* {{{ resume() */
    resume() {
        this.playing = true;
        this.waitForNext();
    }
    /* }}} */
    /* {{{ pause() */
    pause() {
        this.dispatch("pause");

        this.playing = false;
    }
    /* }}} */
    /* {{{ imagesReadyFor() */
    imagesReadyFor(n) {
        return Array.from(this.divs[n].querySelectorAll("img")).every(function(img) {
            return img.complete;
        });
    }
    /* }}} */
    /* {{{ show() */
    show(n, waitForImagesToLoad, customSpeed) {
        waitForImagesToLoad = (typeof waitForImagesToLoad === "undefined") ? this.options.waitForImagesToLoad : waitForImagesToLoad;
        if (waitForImagesToLoad && !this.imagesReadyFor(n)) {
            setTimeout(() => { this.show(n); }, 100);
            return false;
        }
        customSpeed = (typeof customSpeed === "undefined") ? this.options.speed : customSpeed;

        this.dispatch("show", { num: n, total: this.divs.length });

        if (n == this.activeSlide) {
            // slide n is already active
            return;
        }
        this.clearQueue();

        this.divs.forEach((div, i) => {
            if (i != n && i != this.activeSlide) {
                div.style.visibility = "hidden";
            }
        });
        if (canTransition) {
            this.divs.forEach(function(div) {
                div.style.transition = "opacity " + customSpeed + "ms linear";
            });
        }

        // fadeout active slide
        var outgoing = this.divs[this.activeSlide];
        if (outgoing) {
            if (canTransition) {
                // css3 animation
                outgoing.style.opacity = 1;
                oneTransition(outgoing, function() {
                    outgoing.style.visibility = "hidden";
                });
                outgoing.style.opacity = 0;
            } else {
                // no css transitions, just switch
                Object.assign(outgoing.style, {
                    transition: "none",
                    opacity: 0,
                    visibility: "hidden"
                });
            }
        }

        this.activeSlide = n;

        // fadein next slide
        var incoming = this.divs[n];
        Object.assign(incoming.style, {
            visibility: "visible",
            opacity: 0
        });
        if (canTransition) {
            // css3 animation
            oneTransition(incoming, () => {
                this.waitForNext();
            });
            incoming.style.opacity = 1;
        } else {
            // no css transitions, just switch
            Object.assign(incoming.style, {
                transition: "none",
                opacity: 1
            });
            setTimeout(() => { this.waitForNext(); }, customSpeed);
        }
    }
    /* }}} */
    /* {{{ next() */
    next() {
        if (this.activeSlide < this.divs.length - 1) {
            // fade in next image
            this.show(this.activeSlide + 1);
        } else {
            // fade in first image
            this.show(0);
        }
    }
    /* }}} */
    /* {{{ prev() */
    prev() {
        if (this.activeSlide > 0) {
            // fade in previous image
            this.show(this.activeSlide - 1);
        } else {
            // fade in first image
            this.show(this.divs.length - 1);
        }
    }
    /* }}} */
}

// {{{ initSlideshows()
export function initSlideshows(root = document, options = {}) {
    root.querySelectorAll(".slideshow").forEach(function(el) {
        if (!instances.has(el)) {
            new Slideshow(el, options);
        }
    });
}
// }}}
// {{{ getSlideshow()
export function getSlideshow(el) {
    return instances.get(el);
}
// }}}

// vim:set ft=javascript sw=4 sts=4 fdm=marker :
